import { useColorScheme } from '@myosh/design-system/react';
import type { Edge, EdgeChange, Node, NodeChange } from '@xyflow/react';
import { Controls, ReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type React from 'react';
import { useCallback } from 'react';
import { diagramEdgeTypes } from '../../helpers/edge-util';
import { diagramNodeTypes } from '../../helpers/node-util';
import useDiagramHandlers from '../../hooks/use-diagram-handlers';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { onEdgesChange, onNodesChange, selectEdges, selectMueRecordId, selectNodes } from '../../redux/slices/diagram';
import MoveNodesControl from './controls/move-nodes-control.component';

// We show attribution in the About modal dialog
const proOptions = { hideAttribution: true };

/**
 * Diagram component renders a full-screen diagram using ReactFlow.
 *
 * @returns {JSX.Element} The rendered diagram component.
 *
 * @component
 * @example
 * return (
 *   <Diagram />
 * )
 *
 * @see {@link https://reactflow.dev/|ReactFlow Documentation}
 */
const Diagram = (): React.JSX.Element => {
  const { handleNodeClick, handleClick, handleKeyDownEvent } = useDiagramHandlers();
  const nodes = useAppSelector(selectNodes);
  const edges = useAppSelector(selectEdges);
  const mueRecordId = useAppSelector(selectMueRecordId);
  const dispatch = useAppDispatch();
  const [colorScheme] = useColorScheme();

  const handleOnNodesChange = useCallback((nodeChanges: NodeChange<Node>[]) => {
    // Delay the dispatch to prevent ResizeObserver loop issue: https://github.com/xyflow/xyflow/issues/3076
    setTimeout(() => dispatch(onNodesChange(nodeChanges)));
  }, []);

  const handleOnEdgesChange = useCallback((edgeChanges: EdgeChange<Edge>[]) => {
    // Delay the dispatch to prevent ResizeObserver loop issue: https://github.com/xyflow/xyflow/issues/3076
    setTimeout(() => dispatch(onEdgesChange(edgeChanges)));
  }, []);

  return (
    <ReactFlow
      data-cy="diagram-root"
      className="nowheel"
      colorMode={colorScheme}
      nodes={nodes}
      edges={edges}
      nodeTypes={diagramNodeTypes}
      edgeTypes={diagramEdgeTypes}
      onNodesChange={handleOnNodesChange}
      onEdgesChange={handleOnEdgesChange}
      onNodeClick={handleNodeClick}
      onClick={handleClick}
      onKeyDown={handleKeyDownEvent}
      minZoom={0}
      fitView
      panOnDrag
      edgesFocusable={false}
      nodesDraggable={false}
      nodesConnectable={false}
      nodesFocusable={false}
      draggable={false}
      elementsSelectable={false}
      zoomOnDoubleClick={false}
      proOptions={proOptions}
    >
      <Controls showInteractive={false}>{!mueRecordId && <MoveNodesControl />}</Controls>
    </ReactFlow>
  );
};

export default Diagram;
